import type { PrismaClient } from "@prisma/client";

/**
 * Role keys the codebase itself reasons about. These ten rows are seeded by
 * the roles migration with `builtin: true`; the Roles dialog can relabel or
 * deactivate them but never delete them, so every permission check below can
 * assume they exist.
 *
 * A custom role is an admin-created Role row whose `basedOn` names one of
 * these. It carries exactly that builtin's permissions — resolution happens
 * once, in resolveRoleKeys(), so no permission check anywhere else needs to
 * know that custom roles exist at all.
 */
export const BUILTIN_ROLE_KEYS = [
  "super_admin",
  "org_admin",
  "facility_admin",
  "manager",
  "nurse",
  "nurse_aid",
  "caregiver",
  "doctor",
  "patient",
  "family",
] as const;

export type BuiltinRoleKey = (typeof BUILTIN_ROLE_KEYS)[number];

export function isBuiltinRoleKey(key: string): key is BuiltinRoleKey {
  return (BUILTIN_ROLE_KEYS as readonly string[]).includes(key);
}

/** Roles that must be scoped to a facility. */
export const FACILITY_ROLE_KEYS = [
  "facility_admin",
  "manager",
  "nurse",
  "nurse_aid",
  "caregiver",
] as const;

/** Roles allowed to browse facility/organization-scoped care directories. */
export const CARE_DIRECTORY_ROLE_KEYS = [
  "super_admin",
  "org_admin",
  ...FACILITY_ROLE_KEYS,
] as const;

/** Roles allowed to change patient records after scope checks pass. */
export const PATIENT_MANAGER_ROLE_KEYS = [
  "super_admin",
  "org_admin",
  "facility_admin",
  "manager",
  "nurse",
] as const;

/**
 * Roles allowed to move a patient to another facility. Org admins stay inside
 * their organization; only super_admin may transfer across organizations.
 */
export const PATIENT_TRANSFER_ROLE_KEYS = ["super_admin", "org_admin"] as const;

/** Roles allowed to change doctor reach and on-call coverage. */
export const DOCTOR_DIRECTORY_MANAGER_ROLE_KEYS = [
  "super_admin",
  "org_admin",
  "facility_admin",
] as const;

/**
 * Roles that make up a facility's staff roster — the ones the /staff
 * page creates and links. Deliberately excludes doctor (telehealth-scoped,
 * granted from the account page) and the admin tiers.
 */
export const STAFF_ROLE_KEYS = ["manager", "nurse", "nurse_aid", "caregiver"] as const;

/**
 * Roles that may be linked to one or more facilities in the same organization.
 * Wider than STAFF_ROLE_KEYS: facility_admin and doctor use the same
 * checkbox editor (a doctor with no facilities ticked stays global).
 */
export const FACILITY_LINK_ROLE_KEYS = [
  "facility_admin",
  ...STAFF_ROLE_KEYS,
  "doctor",
] as const;

/**
 * Effective builtin roles allowed to sign in at a CareStation unit (PIN or
 * password). Scope is still enforced separately: super_admin is global,
 * org_admin covers units at facilities in their org, facility/staff roles
 * need a matching facility or org grant.
 */
export const KIOSK_LOGIN_ROLE_KEYS = [
  "super_admin",
  "org_admin",
  "facility_admin",
  "manager",
  "nurse",
  "nurse_aid",
  "caregiver",
] as const;

/** Roles allowed to reach the account-management pages at all. */
export const ACCOUNT_ADMIN_ROLE_KEYS = [
  "super_admin",
  "org_admin",
  "facility_admin",
  "manager",
] as const;

export interface ResolvedRole {
  /** The key actually granted — may be a custom role. */
  key: string;
  /** The builtin key whose permissions apply. Equals `key` for builtin roles. */
  effective: BuiltinRoleKey;
  label: string;
  active: boolean;
  builtin: boolean;
}

/**
 * Resolves granted role keys to the builtin permissions they carry.
 *
 * A custom role resolves through `basedOn`; a role row that has gone missing,
 * or a custom role whose base is not a builtin, resolves to null and is
 * dropped by the caller. Dropping is the safe direction: an unresolvable role
 * grants nothing rather than accidentally granting everything.
 *
 * `active: false` is intentionally NOT filtered here. Deactivating a role hides
 * it from the pickers that hand out new grants; it does not strip access from
 * people who already hold it, which would silently lock out staff mid-shift.
 */
export async function resolveRoleKeys(
  db: PrismaClient,
  keys: string[],
): Promise<Map<string, ResolvedRole>> {
  const unique = [...new Set(keys)];
  if (unique.length === 0) return new Map();

  const rows = await db.role.findMany({
    where: { key: { in: unique } },
    select: { key: true, label: true, active: true, builtin: true, basedOn: true },
  });

  const resolved = new Map<string, ResolvedRole>();
  for (const row of rows) {
    const effective = row.builtin ? row.key : (row.basedOn ?? "");
    if (!isBuiltinRoleKey(effective)) continue;
    resolved.set(row.key, {
      key: row.key,
      effective,
      label: row.label,
      active: row.active,
      builtin: row.builtin,
    });
  }
  return resolved;
}
